import type { StateCreator } from "zustand/vanilla";

import { DEFAULT_ENABLED_POI_GROUP_IDS, POI_GROUP_IDS } from "../../studio/poiGroups";
import { DEFAULT_TERRAIN, type TerrainVariant } from "../../studio/searchParams";

/**
 * POIS_ENRICHMENT_SLUG is the enrichment overlay slug whose tile source carries
 * every POI layer. The grouped per-layer state (activePoiGroups) sits on top of
 * this whole-source toggle: the source must be present iff at least one group is
 * enabled, so every POI action reconciles this slug in activeEnrichments. Held
 * as a constant so the reconcile and the seed read one literal.
 */
const POIS_ENRICHMENT_SLUG = "pois";

/**
 * toggleSlug returns the slug list with `slug` added (deduped, appended) when
 * `enabled`, or removed when not. It is the one add/remove-a-slug primitive the
 * enrichment toggle, the per-group POI toggle, and the POIs-membership reconcile
 * all share, so the dedup-on-add / filter-on-remove behavior lives in one place
 * rather than re-inlined per call site. Returns the input array unchanged when
 * enabling an already-present slug, so a no-op toggle preserves referential
 * identity (the dedup-safe contract the enrichment tests pin).
 */
const toggleSlug = (slugs: readonly string[], slug: string, enabled: boolean): string[] => {
  if (enabled) {
    return slugs.includes(slug) ? [...slugs] : [...slugs, slug];
  }

  return slugs.filter((active) => active !== slug);
};

/**
 * reconcilePoisEnrichment returns the enrichment slug list with the POIs source
 * present iff at least one POI group is enabled. It is the safety guard that
 * keeps the whole-source overlay in lockstep with the grouped per-layer state:
 * turning the last group off removes the source (nothing left to render),
 * turning one back on re-adds it. Every POI action funnels through this so the
 * invariant `"pois" ∈ activeEnrichments ⇔ activePoiGroups.length > 0` always
 * holds. Built on the shared toggleSlug primitive.
 */
const reconcilePoisEnrichment = (enrichments: string[], groups: readonly string[]): string[] => {
  const shouldHavePois = groups.length > 0;

  // Preserve referential identity when the POIs membership does not change — a
  // mid-state group toggle leaves the source enabled, and a fresh array would
  // make useStudioEnrichment re-run and remove + re-add the whole POIs source,
  // flashing every layer visible for a frame before the per-layer visibility
  // re-hides the off groups.
  if (enrichments.includes(POIS_ENRICHMENT_SLUG) === shouldHavePois) {
    return enrichments;
  }

  return toggleSlug(enrichments, POIS_ENRICHMENT_SLUG, shouldHavePois);
};

/**
 * uniquePoiGroups returns the given group ids restricted to the known POI group
 * vocabulary and de-duplicated, preserving first-seen order. The wholesale
 * setter (and, through it, the restore path) runs every group set through this
 * so a stale or duplicated id from a snapshot or an agent call cannot land an
 * unknown group in the store. The membership reconcile keys off the resulting
 * length, so dropping unknown ids keeps the `pois` invariant honest.
 */
const uniquePoiGroups = (groups: readonly string[]): string[] => {
  const known = new Set<string>(POI_GROUP_IDS);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const group of groups) {
    if (known.has(group) && !seen.has(group)) {
      seen.add(group);
      result.push(group);
    }
  }

  return result;
};

/**
 * LayersSliceState is the base/overlay layer picture: the active overlay slugs,
 * the explicit overlay category, the enabled POI groups, the shared overlay
 * timestamp, the base terrain + its variant, and the POI-filter notice nonce.
 */
export interface LayersSliceState {
  activeEnrichments: string[];
  activeOverlay: string | null;
  /**
   * activeOverlayCategorySlug is the layers-panel overlay category the user has
   * explicitly selected (or an agent has set), or null when no explicit choice
   * is set — the panel then falls back to the category that owns activeOverlay,
   * or the first manifest category. Decoupling the explicit choice from the
   * active overlay keeps a category switch sticky: browsing a different
   * category with an overlay active no longer snaps the panel back to the
   * overlay's category.
   */
  activeOverlayCategorySlug: string | null;
  /**
   * activePoiGroups are the enabled POI group ids — the grouped per-layer
   * visibility that sits on top of the whole-source `pois` enrichment. The two
   * are kept in lockstep by every POI action: `"pois"` is present in
   * activeEnrichments iff this list is non-empty, so an empty list removes the
   * source (nothing to render) and a non-empty one keeps it. Seeded to the
   * default-on critical subset (water, toilets, parking).
   */
  activePoiGroups: string[];
  overlayTimestamp: string | null;
  /**
   * poiFilterNoticeNonce is a monotonic counter the pick-from-POI flow bumps
   * when it arms a POI pick while SOME (but not all) POI groups are visible —
   * the signal the UI reads to flash a "some POIs are hidden by your filter"
   * notice so the user knows a desired feature may not be on the map. A counter
   * (not a boolean) so a repeat bump always re-fires the notice even if the
   * previous one is still showing; consumers subscribe to the value changing.
   */
  poiFilterNoticeNonce: number;
  terrain: string;
  terrainVariant: TerrainVariant;
}

/**
 * LayersSliceActions are the overlay/terrain/POI setters, reached only through
 * the tool bus. The POI actions keep the whole-source `pois` enrichment in
 * lockstep with the grouped per-layer visibility.
 */
export interface LayersSliceActions {
  /**
   * bumpPoiFilterNotice increments poiFilterNoticeNonce by one so the UI
   * re-flashes the "some POIs are hidden by your filter" notice. The
   * pick-from-POI flow calls it when it arms a POI pick while some (but not
   * all) groups are visible; a counter rather than a flag so a repeat call
   * always re-fires the notice.
   */
  bumpPoiFilterNotice: () => void;
  setActiveOverlay: (overlay: string | null) => void;
  setActiveOverlayCategorySlug: (slug: string | null) => void;
  /**
   * setAllPoiGroups enables every POI group (passing true) or hides every group
   * (passing false). The show-all / hide-all switch the overlay menu header
   * drives and the pick-from-POI flow calls to reveal the full POI set. Reuses
   * setPoiGroups with the full id list or an empty one, so the `pois`
   * enrichment reconcile runs the same way.
   */
  setAllPoiGroups: (enabled: boolean) => void;
  setEnrichment: (slug: string, enabled: boolean) => void;
  setOverlayTimestamp: (timestamp: string | null) => void;
  /**
   * setPoiGroup adds (enabled) or removes (disabled) a single POI group, then
   * reconciles the `pois` enrichment so the source is present iff any group is
   * enabled. The per-toggle path the overlay menu rows drive.
   */
  setPoiGroup: (group: string, enabled: boolean) => void;
  /**
   * setPoiGroups replaces the enabled POI group set wholesale with the given
   * ids (restricted to the known vocabulary, de-duplicated), then reconciles
   * the `pois` enrichment. It is the wholesale setter the snapshot restore
   * uses to return the pre-session POI visibility, and the base setPoiGroup /
   * setAllPoiGroups build on.
   */
  setPoiGroups: (groups: string[]) => void;
  setTerrain: (terrain: string) => void;
  setTerrainVariant: (variant: TerrainVariant) => void;
}

/**
 * layersInitialState seeds the default terrain, the light variant, no active
 * overlay, and the default-on critical POI groups (with the `pois` source kept
 * in lockstep).
 */
export const layersInitialState: LayersSliceState = {
  // Seeded with the POIs source so the default-on critical groups render on the
  // first paint; it stays in lockstep with activePoiGroups below (non-empty), so
  // the `"pois" ∈ activeEnrichments ⇔ activePoiGroups.length > 0` invariant holds
  // from initial state.
  activeEnrichments: [POIS_ENRICHMENT_SLUG],
  activeOverlay: null,
  activeOverlayCategorySlug: null,
  activePoiGroups: [...DEFAULT_ENABLED_POI_GROUP_IDS],
  overlayTimestamp: null,
  poiFilterNoticeNonce: 0,
  terrain: DEFAULT_TERRAIN,
  terrainVariant: "light",
};

/**
 * createLayersSlice builds the overlay/terrain/POI setters. The POI actions
 * funnel through reconcilePoisEnrichment so the whole-source `pois` enrichment
 * stays present iff any group is enabled, preserving referential identity on a
 * no-op membership change.
 */
export const createLayersSlice: StateCreator<
  LayersSliceState & LayersSliceActions,
  [],
  [],
  LayersSliceActions
> = (set, get) => ({
  bumpPoiFilterNotice: () =>
    set((state) => ({ poiFilterNoticeNonce: state.poiFilterNoticeNonce + 1 })),

  setActiveOverlay: (activeOverlay) => set({ activeOverlay }),
  setActiveOverlayCategorySlug: (activeOverlayCategorySlug) => set({ activeOverlayCategorySlug }),

  setAllPoiGroups: (enabled) => {
    // Delegate to the wholesale setter so the `pois` enrichment reconcile and
    // the unique/known-id filtering run one way: enable every known group, or
    // clear the set (which removes the source).
    get().setPoiGroups(enabled ? [...POI_GROUP_IDS] : []);
  },

  setEnrichment: (slug, enabled) =>
    set((state) => {
      const next = toggleSlug(state.activeEnrichments, slug, enabled);
      // Preserve referential identity on a no-op toggle (enabling an
      // already-present slug) so a subscriber's equality check does not
      // re-fire — the dedup-safe contract the enrichment tests pin.
      if (
        next.length === state.activeEnrichments.length &&
        next.every((value, index) => value === state.activeEnrichments[index])
      ) {
        return { activeEnrichments: state.activeEnrichments };
      }

      return { activeEnrichments: next };
    }),

  setOverlayTimestamp: (overlayTimestamp) => set({ overlayTimestamp }),

  setPoiGroup: (group, enabled) =>
    set((state) => {
      const groups = toggleSlug(state.activePoiGroups, group, enabled);

      return {
        activeEnrichments: reconcilePoisEnrichment(state.activeEnrichments, groups),
        activePoiGroups: groups,
      };
    }),

  setPoiGroups: (groups) =>
    set((state) => {
      const next = uniquePoiGroups(groups);

      return {
        activeEnrichments: reconcilePoisEnrichment(state.activeEnrichments, next),
        activePoiGroups: next,
      };
    }),

  setTerrain: (terrain) => set({ terrain }),
  setTerrainVariant: (terrainVariant) => set({ terrainVariant }),
});
